import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import AlertsContainer from "components/Alerts";
import SideTray from "components/SideTray";
import { isRTL } from "utils/getLanguageDir";
import Notifications from "components/Notifications";
import TaskBar from "components/Taskbar/Taskbar";
import { isAuthenticated } from "data/app/appSelectors";
import { useAlertsHandler } from "hooks/useAlertsHandler";
import { getOffset, useTimezoneOperations } from "hooks/useTimezone";
import SidebarContext, { SidebarContextProps } from "contexts/SidebarContext";

function DashboardWrapperLayout({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation();
	const [isSidebarOpen, setIsSidebarOpen] = useState(true);
	const [isConfigOpen, setIsConfigOpen] = useState(false);
	const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

	const auth = useSelector(isAuthenticated);
	const { addAlert } = useAlertsHandler();

	// Handles the state for the SideBar in the app
	const sidebarContext: SidebarContextProps = useMemo(
		() => ({
			isSidebarOpen,
			setIsConfigOpen,
			setIsSidebarOpen,
			isConfigOpen,
		}),
		[isConfigOpen, isSidebarOpen]
	);

	const { timezone, setTimezone } = useTimezoneOperations();

	useEffect(() => {
		const browserTimezone =
			Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (!timezone) {
			setTimezone(browserTimezone);
		} else {
			if (getOffset(timezone) !== getOffset(browserTimezone)) {
				addAlert("timezonesMismatch", "info", 5000);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<SidebarContext.Provider value={sidebarContext}>
			<div className="app" key="app_container">
				{auth && (
					<>
						{/* Notifications */}
						<SideTray
							title={t("main.notifications.heading")}
							className="notifications__sidetray"
							align={isRTL() ? "left" : "right"}
							isTrayOpen={isNotificationsOpen}
							setIsTrayOpen={setIsNotificationsOpen}>
							<Notifications
								setIsNotificationsTrayOpen={
									setIsNotificationsOpen
								}
							/>
						</SideTray>
						{/* Top nav (the mobile SideBar lives in this component and the desktop version lives in DashboardLayout) */}
						<TaskBar
							setIsNotificationsTrayOpen={setIsNotificationsOpen}
						/>
						{/* Alerts */}
						<AlertsContainer isNotificationsTrayOpen={false} />
					</>
				)}
				{children}
			</div>
		</SidebarContext.Provider>
	);
}

export default DashboardWrapperLayout;
